import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

// Imported directly instead of via '../../components': TrustSealsBanner is mounted
// inside LayoutSingleColumn, which most of the barrel file's exports pull in.
import ExternalLink from '../ExternalLink/ExternalLink';
import IconCancel from '../IconCancel/IconCancel';
import TrustSeals, { KONSUMENTENBUND_PROFILE_URL } from '../TrustSeals/TrustSeals';

import css from './TrustSealsBanner.module.css';

const DISMISSED_STORAGE_KEY = 'toastablyTrustSealsBannerDismissed';

/**
 * Announcement banner surfacing the Konsumentenbund trust seals to
 * first-time visitors on small and medium screens; on desktop the floating
 * TrustBadgesPanel takes over (the client wants the header treatment on
 * mobile only). The banner renders in normal flow above the sticky topbar,
 * so it scrolls away with the page instead of taking permanent viewport
 * space, and a dismissal is remembered across visits in localStorage.
 *
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 */
const TrustSealsBanner = props => {
  const { rootClassName, className } = props;
  const intl = useIntl();
  // SSR always renders the banner; a previous dismissal is only readable from
  // localStorage after mount.
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    try {
      if (window.localStorage.getItem(DISMISSED_STORAGE_KEY)) {
        setIsDismissed(true);
      }
    } catch (e) {
      // localStorage can be unavailable (e.g. blocked storage); show the banner.
    }
  }, []);

  const onDismiss = () => {
    setIsDismissed(true);
    try {
      window.localStorage.setItem(DISMISSED_STORAGE_KEY, '1');
    } catch (e) {
      // Without storage the dismissal just lasts for the current page view.
    }
  };

  if (isDismissed) {
    return null;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <TrustSeals className={css.seals} />
      <p className={css.text}>
        <span className={css.textDesktop}>
          <FormattedMessage id="TrustSealsBanner.text" />
        </span>
        <span className={css.textMobile}>
          <FormattedMessage id="TrustSealsBanner.textMobile" />
        </span>
      </p>
      <ExternalLink className={css.learnMore} href={KONSUMENTENBUND_PROFILE_URL}>
        <FormattedMessage id="TrustSealsBanner.learnMore" />
      </ExternalLink>
      <button
        className={css.closeButton}
        type="button"
        onClick={onDismiss}
        aria-label={intl.formatMessage({ id: 'TrustSealsBanner.close' })}
      >
        <IconCancel />
      </button>
    </div>
  );
};

export default TrustSealsBanner;
